import React, { useState, useEffect } from 'react';
import { getServiceProfitabilityReport, getServiceCategories, getServices } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  PieChart,
  RefreshCw,
  Filter,
  Download,
  X,
  Loader2,
  Award,
  AlertTriangle,
  Percent,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, cn } from '@/lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';

const COLORS = ['hsl(188, 64%, 38%)', 'hsl(164, 62%, 40%)', 'hsl(38, 95%, 55%)', 'hsl(4, 79%, 55%)', 'hsl(205, 92%, 45%)', 'hsl(280, 60%, 50%)', 'hsl(150, 60%, 45%)', 'hsl(220, 70%, 50%)'];

function StatCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = "primary", loading }) {
  const colorMap = {
    primary: 'from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)]',
    success: 'from-emerald-500 to-green-500',
    warning: 'from-amber-500 to-orange-500',
    danger: 'from-red-500 to-rose-500',
    info: 'from-blue-500 to-cyan-500',
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-8 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300" data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={cn('absolute inset-x-0 top-0 h-1 bg-gradient-to-r', colorMap[color])} />
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {trend && trendValue && (
              <div className={cn(
                'flex items-center gap-1 mt-2 text-xs font-medium',
                trend === 'up' ? 'text-emerald-600' : 'text-red-500'
              )}>
                {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {trendValue}
              </div>
            )}
          </div>
          <div className={cn('p-3 rounded-xl bg-gradient-to-br text-white shadow-lg group-hover:scale-105 transition-transform', colorMap[color])}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border rounded-lg shadow-lg p-3">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey || entry.name} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.name.includes('Revenue') || entry.name.includes('Profit') ? formatCurrency(entry.value) : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function ServiceProfitabilityPage() {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    category_id: 'all',
    service_id: 'all'
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [catRes, svcRes] = await Promise.all([
        getServiceCategories().catch(() => ({ data: [] })),
        getServices().catch(() => ({ data: { items: [] } }))
      ]);
      setCategories(catRes.data || []);
      setServices(svcRes.data?.items || svcRes.data || []);
      await loadReport();
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load initial data');
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.category_id && filters.category_id !== 'all') params.category_id = filters.category_id;
      if (filters.service_id && filters.service_id !== 'all') params.service_id = filters.service_id;
      
      const response = await getServiceProfitabilityReport(params);
      setReportData(response.data);
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Failed to load profitability report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    loadReport();
  };

  const handleClearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      category_id: 'all',
      service_id: 'all'
    });
  };

  const summary = reportData?.summary || {};
  const servicesData = reportData?.services || [];
  const monthlyTrends = reportData?.monthly_trends || [];
  const comparisonData = reportData?.comparison || [];

  // Prepare chart data
  const profitDistributionData = servicesData.slice(0, 8).map((s, i) => ({
    name: s.service_name?.substring(0, 15) + (s.service_name?.length > 15 ? '...' : ''),
    profit: s.total_profit,
    revenue: s.total_revenue,
    fill: COLORS[i % COLORS.length]
  }));

  const marginComparisonData = servicesData.map(s => ({
    name: s.service_name?.substring(0, 12) + (s.service_name?.length > 12 ? '...' : ''),
    margin: s.profit_margin_pct,
    volume: s.total_volume
  })).sort((a, b) => b.margin - a.margin).slice(0, 10);

  return (
    <div className="space-y-6" data-testid="service-profitability-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Service Profitability Report
          </h1>
          <p className="text-muted-foreground mt-1">
            Analyze profit/loss, margins, and trends across all services
          </p>
        </div>
        <Button variant="outline" onClick={loadReport} disabled={loading} className="gap-2">
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card data-testid="filters-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-muted-foreground" />
              <CardTitle className="text-lg">Filters</CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClearFilters} className="gap-1 text-muted-foreground">
              <X className="w-4 h-4" /> Clear
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={filters.category_id} onValueChange={(v) => handleFilterChange('category_id', v)}>
                <SelectTrigger data-testid="filter-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Service</Label>
              <Select value={filters.service_id} onValueChange={(v) => handleFilterChange('service_id', v)}>
                <SelectTrigger data-testid="filter-service">
                  <SelectValue placeholder="All Services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {services.map(s => (
                    <SelectItem key={s.service_code} value={s.service_code}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={handleApplyFilters} disabled={loading} className="w-full gap-2 bg-gradient-to-r from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)]">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Apply
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Total Revenue"
          value={formatCurrency(summary.total_revenue || 0)}
          icon={DollarSign}
          color="info"
          loading={loading}
        />
        <StatCard
          title="Total Profit"
          value={formatCurrency(summary.total_profit || 0)}
          icon={TrendingUp}
          color={summary.total_profit >= 0 ? "success" : "danger"}
          loading={loading}
        />
        <StatCard
          title="Avg Profit Margin"
          value={`${summary.avg_profit_margin || 0}%`}
          icon={Percent}
          color="primary"
          loading={loading}
        />
        <StatCard
          title="Profitable Services"
          value={summary.profitable_services || 0}
          subtitle={`of ${summary.total_services || 0} total`}
          icon={Award}
          color="success"
          loading={loading}
        />
        <StatCard
          title="Loss-Making Services"
          value={summary.loss_making_services || 0}
          icon={AlertTriangle}
          color="danger"
          loading={loading}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Monthly Revenue & Profit Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : monthlyTrends.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={monthlyTrends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="total_revenue" name="Revenue" fill="hsl(188, 64%, 38%)" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="total_profit" name="Profit" stroke="hsl(164, 62%, 40%)" strokeWidth={3} dot={{ fill: 'hsl(164, 62%, 40%)' }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Profit Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Profit Distribution by Service
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : profitDistributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPie>
                  <Pie
                    data={profitDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="profit"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: 'hsl(var(--muted-foreground))' }}
                  >
                    {profitDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Margin Comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Profit Margin by Service (Top 10)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : marginComparisonData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={marginComparisonData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Bar dataKey="margin" name="Profit Margin %" fill="hsl(164, 62%, 40%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Month-over-Month Comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Month-over-Month Changes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : comparisonData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}%`} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => `${value > 0 ? '+' : ''}${value}%`} />
                  <Legend />
                  <Area type="monotone" dataKey="revenue_change_pct" name="Revenue Change %" fill="hsl(188, 64%, 38%)" fillOpacity={0.3} stroke="hsl(188, 64%, 38%)" />
                  <Area type="monotone" dataKey="profit_change_pct" name="Profit Change %" fill="hsl(164, 62%, 40%)" fillOpacity={0.3} stroke="hsl(164, 62%, 40%)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Need at least 2 months of data
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top & Bottom Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-600">
              <Award className="w-5 h-5" />
              Top Performing Services
            </CardTitle>
            <CardDescription>Highest profit generators</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {loading ? (
                Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)
              ) : (summary.top_performers || []).map((svc, idx) => (
                <div key={svc.service_name || idx} className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center font-bold">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-medium text-sm">{svc.service_name}</p>
                      <p className="text-xs text-muted-foreground">{svc.total_volume} units</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-emerald-600">{formatCurrency(svc.total_profit)}</p>
                    <p className="text-xs text-muted-foreground">{svc.profit_margin_pct}% margin</p>
                  </div>
                </div>
              ))}
              {!loading && (summary.top_performers || []).length === 0 && (
                <p className="text-center text-muted-foreground py-4">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bottom Performers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Bottom Performing Services
            </CardTitle>
            <CardDescription>Services needing attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {loading ? (
                Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)
              ) : (summary.bottom_performers || []).map((svc, idx) => (
                <div key={svc.service_name || idx} className="flex items-center justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30">
                  <div className="flex items-center gap-3">
                    <span className={cn(
                      "w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold",
                      svc.total_profit < 0 ? "bg-red-500" : "bg-amber-500"
                    )}>
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-medium text-sm">{svc.service_name}</p>
                      <p className="text-xs text-muted-foreground">{svc.total_volume} units</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={cn("font-semibold", svc.total_profit < 0 ? "text-red-600" : "text-amber-600")}>
                      {formatCurrency(svc.total_profit)}
                    </p>
                    <p className="text-xs text-muted-foreground">{svc.profit_margin_pct}% margin</p>
                  </div>
                </div>
              ))}
              {!loading && (summary.bottom_performers || []).length === 0 && (
                <p className="text-center text-muted-foreground py-4">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Services Profitability Detail</CardTitle>
          <CardDescription>{servicesData.length} services analyzed</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Service</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Volume</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Govt Fees</TableHead>
                  <TableHead className="text-right">Service Fees</TableHead>
                  <TableHead className="text-right">Profit</TableHead>
                  <TableHead className="text-right">Margin %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array(5).fill(0).map((_, i) => (
                    <TableRow key={i}>
                      {Array(8).fill(0).map((_, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : servicesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No service data available for the selected period
                    </TableCell>
                  </TableRow>
                ) : servicesData.map((svc) => (
                  <TableRow key={svc.service_code || svc.service_name} className="hover:bg-muted/30">
                    <TableCell>
                      <div>
                        <p className="font-medium">{svc.service_name}</p>
                        <p className="text-xs text-muted-foreground">{svc.service_code}</p>
                      </div>
                    </TableCell>
                    <TableCell>{svc.category_name}</TableCell>
                    <TableCell className="text-right font-mono">{svc.total_volume}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(svc.total_revenue)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(svc.total_govt_paid)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(svc.total_service_fee)}</TableCell>
                    <TableCell className={cn(
                      "text-right font-mono font-semibold",
                      svc.total_profit >= 0 ? "text-emerald-600" : "text-red-600"
                    )}>
                      {formatCurrency(svc.total_profit)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={svc.profit_margin_pct >= 20 ? "default" : svc.profit_margin_pct >= 0 ? "secondary" : "destructive"}>
                        {svc.profit_margin_pct}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Report Generated Info */}
      {reportData?.generated_at && (
        <p className="text-center text-xs text-muted-foreground">
          Report generated at {new Date(reportData.generated_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}
